import { Box, Center, Loader, Paper, Stack, Text } from "@mantine/core";
import AppPaginationBar from "@/components/shared/AppPaginationBar";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import InterestRequestsTable, {
  type InterestRequestTableRow,
} from "@/pages/interests/components/InterestRequestsTable";
import type { RequestFilter } from "@/pages/dashboard/dashboardTab.const";

const EMPTY_PANEL_STYLE = {
  borderColor: "#04305c",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
} as const;

export function filterVisibleInterestRequests(requests: unknown[] | undefined, _isRequester: boolean) {
  return (requests ?? []).filter((r: any) => {
    const s = String(r?.status ?? "").trim();
    if (s === CollaborationStatus.REQUESTED) return true;
    if (s === CollaborationStatus.RECEIVER_DECLINED) return true;
    return false;
  });
}

export interface InterestRequestsPanelProps {
  requestFilter?: RequestFilter;
  loading: boolean;
  requests?: any[];
  isRequester?: boolean;
  totalPages?: number;
  totalCount?: number;
  page?: number;
  onPageChange?: (p: number) => void;
  emptyLabel: string;
  actionLoading: string | null;
  onAccept: (matchId: string) => void;
  onDecline: (matchId: string) => void;
  onMakePayment: (matchId: string) => void;
  onViewProfile: (companyId: string) => void;
  onOpenWorkspace: (matchId: string) => void;
  /** Admin-suggested partners (same table; profile opens in collaboration workspace). */
  suggestedRows?: InterestRequestTableRow[];
  /** Received + sent + suggested rows (ALL filter). */
  combinedRows?: InterestRequestTableRow[];
  /** Called when a user dismisses a suggested partner (passes recommendationId). */
  onDismissSuggestion?: (recommendationId: string) => void;
  /** Set of recommendationIds currently being dismissed (shows loading state). */
  dismissingIds?: Set<string>;
}

export default function InterestRequestsPanel({
  requestFilter = "all",
  loading,
  requests = [],
  isRequester = false,
  totalPages = 1,
  totalCount = 0,
  page = 1,
  onPageChange = () => {},
  emptyLabel,
  actionLoading,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
  suggestedRows,
  combinedRows,
  onDismissSuggestion,
  dismissingIds,
}: InterestRequestsPanelProps) {
  const isCombinedMode = combinedRows !== undefined;
  const isSuggestedMode = !isCombinedMode && suggestedRows !== undefined;
  const visibleRequests = isCombinedMode || isSuggestedMode
    ? []
    : filterVisibleInterestRequests(requests ?? [], isRequester);

  const effectiveEmptyLabel =
    !isCombinedMode &&
    !isSuggestedMode &&
    visibleRequests.length === 0 &&
    (requests?.length ?? 0) > 0
      ? "No pending requests — accepted items are now in Collaborations."
      : emptyLabel;

  const combinedEmpty = isCombinedMode && (combinedRows?.length ?? 0) === 0;
  const suggestedEmpty = isSuggestedMode && (suggestedRows?.length ?? 0) === 0;

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      {isCombinedMode ? (
        combinedEmpty ? (
          <Paper p="xl" radius="lg" style={EMPTY_PANEL_STYLE}>
            <Text c="gray.5" ta="center">
              {emptyLabel}
            </Text>
          </Paper>
        ) : (
          <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <InterestRequestsTable
              requestFilter={requestFilter}
              rows={combinedRows}
              isLoading={false}
              actionLoading={actionLoading}
              onAccept={onAccept}
              onDecline={onDecline}
              onMakePayment={onMakePayment}
              onViewProfile={onViewProfile}
              onOpenWorkspace={onOpenWorkspace}
              onDismissSuggestion={onDismissSuggestion}
              dismissingIds={dismissingIds}
            />
          </Box>
        )
      ) : isSuggestedMode ? (
        suggestedEmpty ? (
          <Paper p="xl" radius="lg" style={EMPTY_PANEL_STYLE}>
            <Text c="gray.5" ta="center">
              {emptyLabel}
            </Text>
          </Paper>
        ) : (
          <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <InterestRequestsTable
              requestFilter={requestFilter}
              rows={suggestedRows}
              isLoading={false}
              actionLoading={actionLoading}
              onAccept={onAccept}
              onDecline={onDecline}
              onMakePayment={onMakePayment}
              onViewProfile={onViewProfile}
              onOpenWorkspace={onOpenWorkspace}
              onDismissSuggestion={onDismissSuggestion}
              dismissingIds={dismissingIds}
            />
          </Box>
        )
      ) : visibleRequests.length === 0 ? (
        <Paper p="xl" radius="lg" style={EMPTY_PANEL_STYLE}>
          <Text c="gray.5" ta="center">
            {effectiveEmptyLabel}
          </Text>
        </Paper>
      ) : (
        <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <InterestRequestsTable
            requestFilter={requestFilter}
            requests={visibleRequests}
            isRequester={isRequester}
            isLoading={false}
            actionLoading={actionLoading}
            onAccept={onAccept}
            onDecline={onDecline}
            onMakePayment={onMakePayment}
            onViewProfile={onViewProfile}
            onOpenWorkspace={onOpenWorkspace}
          />
        </Box>
      )}

      {!isCombinedMode && !isSuggestedMode && totalPages > 1 && (
        <Box mt="xl">
          <AppPaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={10}
            onPageChange={onPageChange}
            pageSizeOptions={[10]}
            boundaries={3}
          />
        </Box>
      )}
    </Stack>
  );
}
